import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { listAgreements } from '../../../services/consentService';
import { TERMS_TYPES } from '../../../services/termsService';

const ROLE_LABEL = { partner: '제휴업체', realtor: '공인중개사', admin: '관리자', staff: '직원' };
const TERMS_LABEL_BY_KEY = TERMS_TYPES.reduce((acc, t) => ({ ...acc, [t.key]: t.label }), {});
const METHOD_LABEL = { signup: '회원가입', consent_gate: '재동의', admin: '관리자' };
const PATH_LABEL = {
  'oauth-consent': 'Oauth 동의',
  'landing_signup_partner': '제휴업체 가입',
  'landing_signup_realtor': '공인중개사 가입',
  'consent-gate': '재동의',
  'signup': '회원가입',
  'admin': '관리자',
  'grandfather': '소급 적용',
};
const CONSENT_METHOD_LABEL = { checkbox: '체크박스', signature: '서명', click: '클릭' };
const SOURCE_LABEL = { web: '웹', mobile_webview: '앱' };
const PAGE_SIZE_OPTIONS = [25, 50, 100];

const truncate = (s, n = 40) => (s && s.length > n ? `${s.slice(0, n)}…` : (s || ''));

const formatDateTime = (val) => {
  if (!val) return '-';
  try {
    const d = val.toDate ? val.toDate() : new Date(val);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch {
    return '-';
  }
};

const AdminConsents = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterRole, setFilterRole] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listAgreements({ limit: 1000, role: filterRole || undefined });
      setRows(data);
      setPage(1);
    } catch (e) {
      console.error('[동의 이력 조회 실패]', e);
      alert(e.message || '동의 이력 조회 실패');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterRole]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const name = (r.userDisplayInfo?.name || '').toLowerCase();
      const phone = (r.userDisplayInfo?.phone || '').toLowerCase();
      const email = (r.userDisplayInfo?.email || '').toLowerCase();
      const term = (r.termTitle || TERMS_LABEL_BY_KEY[r.termKey] || r.termKey || '').toLowerCase();
      const termKey = (r.termKey || '').toLowerCase();
      const version = (r.termVersion || '').toLowerCase();
      const method = (METHOD_LABEL[r.method] || r.method || '').toLowerCase();
      return [name, phone, email, term, termKey, version, method].some((s) => s.includes(q));
    });
  }, [rows, search]);

  const summary = useMemo(() => {
    const total = filtered.length;
    const agreed = filtered.filter((r) => r.agreed !== false).length;
    return { total, agreed, refused: total - agreed };
  }, [filtered]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filtered, currentPage, pageSize],
  );

  const onSubmitSearch = (e) => {
    e?.preventDefault?.();
    setSearch(searchInput);
    setPage(1);
  };

  return (
    <Wrap>
      <HeaderRow>
        <div>
          <Crumb>동의 기록</Crumb>
          <Title>동의 기록 목록</Title>
          <Desc>법적 증빙 정보와 원문 검토 시 화면에서 확인할 수 있도록 목록 구조를 정리합니다.</Desc>
        </div>
        <RefreshBtn onClick={load} disabled={loading}>{loading ? '불러오는 중...' : '새로고침'}</RefreshBtn>
      </HeaderRow>

      <SummaryRow>
        <SummaryCard>
          <SummaryLabel>전체 기록</SummaryLabel>
          <SummaryValue>{summary.total.toLocaleString()}건</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>동의함</SummaryLabel>
          <SummaryValue>{summary.agreed.toLocaleString()}건</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>비동의</SummaryLabel>
          <SummaryValue>{summary.refused.toLocaleString()}건</SummaryValue>
        </SummaryCard>
      </SummaryRow>

      <FilterCard onSubmit={onSubmitSearch}>
        <FilterTitle>검색 및 필터</FilterTitle>
        <FilterDesc>회원 정보, 문서명 및 문서 버전, 동의 경로까지 한 번에 검색할 수 있습니다.</FilterDesc>
        <FilterBar>
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="회원 정보, 문서명 및 문서 버전, 경로 검색"
          />
          <SearchBtn type="submit">검색</SearchBtn>
          <Select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
            <option value="">전체 회원 유형</option>
            <option value="partner">제휴업체</option>
            <option value="realtor">공인중개사</option>
            <option value="admin">관리자</option>
            <option value="staff">직원</option>
          </Select>
        </FilterBar>
      </FilterCard>

      <TableCard>
        <TableHead>
          <div>
            <TableTitle>동의 기록 목록</TableTitle>
            <TableDesc>법적 구분, 방식, 경로 등 표준 컬럼으로 표시합니다.</TableDesc>
          </div>
          <PageSizeWrap>
            <PageSizeLabel>페이지당</PageSizeLabel>
            <PageSizeSelect
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            >
              {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}건</option>)}
            </PageSizeSelect>
          </PageSizeWrap>
        </TableHead>

        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th>등록일시</Th>
                <Th>회원유형</Th>
                <Th>이름/상호</Th>
                <Th>연락정보</Th>
                <Th>문서명</Th>
                <Th>문서버전</Th>
                <Th>동의여부</Th>
                <Th>동의방식</Th>
                <Th>동의경로</Th>
                <Th>법적증빙</Th>
                <Th>원문보기</Th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 && !loading && (
                <tr><Td colSpan={11} style={{ textAlign: 'center', color: '#9CA3AF', padding: '32px 0' }}>이력이 없습니다.</Td></tr>
              )}
              {pageRows.map((r) => {
                const cm = r.consentMethod || 'checkbox';
                const pathKey = r.path || (r.method === 'consent_gate' ? 'consent-gate' : r.method) || '';
                const pathLabel = PATH_LABEL[pathKey] || METHOD_LABEL[r.method] || pathKey || '-';
                return (
                  <Row key={r.id}>
                    <Td>{formatDateTime(r.createdAt)}</Td>
                    <Td><RoleChip>{ROLE_LABEL[r.role] || r.role || '-'}</RoleChip></Td>
                    <Td>
                      <Stacked><StackedMain>{r.userDisplayInfo?.name || '-'}</StackedMain></Stacked>
                    </Td>
                    <Td>
                      <Stacked>
                        <StackedMain>{r.userDisplayInfo?.email || '-'}</StackedMain>
                        <StackedSub>{r.userDisplayInfo?.phone || '-'}</StackedSub>
                      </Stacked>
                    </Td>
                    <Td>
                      <Stacked>
                        <StackedMain>{r.termTitle || TERMS_LABEL_BY_KEY[r.termKey] || r.termKey}</StackedMain>
                        <StackedSub>{r.termKey || ''}</StackedSub>
                      </Stacked>
                    </Td>
                    <Td>
                      <Stacked>
                        <StackedMain>{r.termVersion ? `v${r.termVersion}` : '-'}</StackedMain>
                        <StackedSub>{r.termEffectiveDate ? `시행 ${r.termEffectiveDate}` : ''}</StackedSub>
                      </Stacked>
                    </Td>
                    <Td>
                      {r.agreed === false
                        ? <AgreeChip $no>비동의</AgreeChip>
                        : <AgreeChip>동의함</AgreeChip>}
                    </Td>
                    <Td>
                      <Stacked>
                        <StackedMain>{CONSENT_METHOD_LABEL[cm] || cm}</StackedMain>
                        <StackedSub>{cm}</StackedSub>
                      </Stacked>
                    </Td>
                    <Td>
                      <Stacked>
                        <StackedMain>{pathLabel}</StackedMain>
                        <StackedSub>{pathKey || '-'}</StackedSub>
                      </Stacked>
                    </Td>
                    <Td>
                      <Stacked>
                        <StackedMain>IP {r.ip || '-'}</StackedMain>
                        <StackedSub>브라우저 {truncate(r.userAgent, 28) || '-'}</StackedSub>
                      </Stacked>
                    </Td>
                    <Td><ViewBtn onClick={() => setDetail(r)}>+ 원문 보기</ViewBtn></Td>
                  </Row>
                );
              })}
            </tbody>
          </Table>
        </TableWrap>

        {filtered.length > 0 && (
          <Pagination>
            <PageBtn disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>이전</PageBtn>
            <PageInfo>{currentPage} / {totalPages}</PageInfo>
            <PageBtn disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>다음</PageBtn>
          </Pagination>
        )}
      </TableCard>

      {detail && (
        <Backdrop onClick={() => setDetail(null)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHead>
              <div>
                <ModalTitle>{detail.termTitle || detail.termKey}</ModalTitle>
                <ModalMeta>
                  버전 {detail.termVersion || '-'} · 시행일 {detail.termEffectiveDate || '-'} · 동의 {formatDateTime(detail.createdAt)}
                </ModalMeta>
              </div>
              <CloseBtn onClick={() => setDetail(null)}>닫기</CloseBtn>
            </ModalHead>
            <UserBox>
              <UserLine><UserKey>이름</UserKey>{detail.userDisplayInfo?.name || '-'}</UserLine>
              <UserLine><UserKey>역할</UserKey>{ROLE_LABEL[detail.role] || detail.role || '-'}</UserLine>
              <UserLine><UserKey>전화</UserKey>{detail.userDisplayInfo?.phone || '-'}</UserLine>
              <UserLine><UserKey>이메일</UserKey>{detail.userDisplayInfo?.email || '-'}</UserLine>
              <UserLine><UserKey>방식</UserKey>{CONSENT_METHOD_LABEL[detail.consentMethod] || detail.consentMethod || 'checkbox'}</UserLine>
              <UserLine><UserKey>경로</UserKey>{PATH_LABEL[detail.path] || METHOD_LABEL[detail.method] || detail.path || detail.method || '-'} · {SOURCE_LABEL[detail.source] || detail.source || '-'}</UserLine>
              <UserLine><UserKey>IP</UserKey>{detail.ip || '-'}</UserLine>
              <UserLine><UserKey>UA</UserKey><Mono>{detail.userAgent || '-'}</Mono></UserLine>
            </UserBox>
            <SnapshotLabel>동의 당시 약관 원문</SnapshotLabel>
            <Snapshot>{detail.contentSnapshot || '(원문 스냅샷이 저장되지 않았습니다)'}</Snapshot>
          </Modal>
        </Backdrop>
      )}
    </Wrap>
  );
};

export default AdminConsents;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const Crumb = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 6px;
`;

const Desc = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const RefreshBtn = styled.button`
  padding: 9px 18px;
  background: #fff;
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  font-weight: 500;
  &:disabled { opacity: 0.6; cursor: default; }
  &:hover:not(:disabled) { background: ${({ theme }) => theme.colors.borderLight}; }
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

const SummaryCard = styled.div`
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: ${({ theme }) => theme.shadow.sm};
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const SummaryValue = styled.div`
  font-size: 26px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
`;

const FilterCard = styled.form`
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: ${({ theme }) => theme.shadow.sm};
  padding: 18px 20px;
`;

const FilterTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const FilterDesc = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 12px;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Input = styled.input`
  flex: 1;
  min-width: 220px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
`;

const SearchBtn = styled.button`
  padding: 10px 20px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  font-weight: 600;
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  background: #fff;
  min-width: 160px;
`;

const TableCard = styled.div`
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: ${({ theme }) => theme.shadow.sm};
  overflow: hidden;
`;

const TableHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const TableTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const TableDesc = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PageSizeWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PageSizeLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PageSizeSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 12px;
  background: #fff;
`;

const TableWrap = styled.div`
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 1400px;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.borderLight};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  white-space: nowrap;
  vertical-align: top;
`;

const Row = styled.tr`
  &:hover { background: ${({ theme }) => theme.colors.borderLight}; }
`;

const RoleChip = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.borderLight};
  color: ${({ theme }) => theme.colors.text};
  font-size: 12px;
  font-weight: 500;
`;

const AgreeChip = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ $no, theme }) => ($no ? '#FEE2E2' : '#DBEAFE')};
  color: ${({ $no }) => ($no ? '#991B1B' : '#1E40AF')};
  font-size: 12px;
  font-weight: 600;
`;

const ViewBtn = styled.button`
  padding: 6px 12px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
  &:hover { background: ${({ theme }) => theme.colors.borderLight}; }
`;

const Mono = styled.code`
  font-family: 'JetBrains Mono', Menlo, monospace;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  word-break: break-all;
`;

const Stacked = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StackedMain = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text};
`;

const StackedSub = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const PageBtn = styled.button`
  padding: 6px 14px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 12px;
  &:disabled { opacity: 0.4; cursor: default; }
  &:hover:not(:disabled) { background: ${({ theme }) => theme.colors.borderLight}; }
`;

const PageInfo = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: grid;
  place-items: center;
  padding: 20px;
  z-index: 1000;
`;

const Modal = styled.div`
  width: min(720px, 100%);
  max-height: 86vh;
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 24px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
`;

const ModalMeta = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CloseBtn = styled.button`
  padding: 6px 12px;
  font-size: 13px;
  background: ${({ theme }) => theme.colors.borderLight};
  color: ${({ theme }) => theme.colors.text};
  border-radius: ${({ theme }) => theme.radius.sm};
`;

const UserBox = styled.div`
  background: ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 12px 14px;
  margin-bottom: 14px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 16px;
`;

const UserLine = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  gap: 8px;
`;

const UserKey = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 36px;
`;

const SnapshotLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;

const Snapshot = styled.div`
  flex: 1;
  overflow: auto;
  padding: 14px 16px;
  background: ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.text};
  white-space: pre-wrap;
`;
